/**
 * The signal ledger.
 *
 * The taste model is a **fold over an append-only ledger**, not a mutable
 * weight vector. That single decision buys both required properties at once:
 *
 *  - **Reversible** — mark entries retracted and recompute. Undoing a layout
 *    change genuinely undoes the signal it produced, rather than stacking an
 *    opposing signal on top of it.
 *  - **Inspectable** — every claim can cite the exact events that produced it.
 *
 * Neither can be retrofitted onto a mutable blob.
 */

import type { KnobId, KnobIndex, Scope } from './knobs.js';

export type SignalClass =
  | 'stated' | 'clone'
  | 'keep' | 'dismiss' | 'not-now'
  | 'pin' | 'release'
  | 'adjust' | 'revert'
  | 'attention' | 'correction';

export interface Observation {
  knob: KnobId;
  index?: KnobIndex;
  value: string;
  /** Signed: positive endorses `value`, negative opposes it. */
  weight?: number;
}

export interface SignalEvent {
  id: string;
  at: number;
  sessionId: string;
  class: SignalClass;
  scope: Scope;
  observations: Observation[];
  cause?: { candidateId?: string; revisionId?: string };
  /** Retracted events stay in the ledger. Audit, not deletion. */
  retractedAt?: number;
}

/**
 * Weights, in units where **one keep = 1.0**, half-lives in days.
 *
 * The ratios are the defensible part; the absolute scale is anchored to that
 * one arbitrary choice and the ~6.2-unit change threshold it implies.
 */
export const SIGNAL: Record<SignalClass, { weight: number; halfLifeDays: number }> = {
  // Exactly the change threshold. If you tell it, it listens, now — anything
  // less makes "inspectable and correctable" a lie.
  stated:     { weight: 6.0,  halfLifeDays: 730 },
  clone:      { weight: 4.0,  halfLifeDays: 365 },
  pin:        { weight: 3.0,  halfLifeDays: Infinity },
  // Hiding the same column three times is the clearest unspoken statement a
  // person can make: it costs effort, it is unambiguous, and it repeats.
  adjust:     { weight: 2.0,  halfLifeDays: 180 },
  keep:       { weight: 1.0,  halfLifeDays: 180 },
  release:    { weight: 1.0,  halfLifeDays: 180 },
  // Weaker than keep, because dismiss is polysemous — "not this", "not now",
  // "stop bothering me". Keep has one meaning. Asymmetry belongs on ambiguity.
  dismiss:    { weight: -0.6, halfLifeDays: 180 },
  'not-now':  { weight: -0.2, halfLifeDays: 90 },
  // Stronger than the keep that preceded it: regret is better evidence than
  // agreement, because it survived use rather than a three-second preview.
  revert:     { weight: -2.5, halfLifeDays: 365 },
  // Deliberately inert in v1. Dwell measures difficulty at least as much as
  // interest — a confusing layout attracts more of it. Enable only once there
  // is data showing it correlates with keep.
  attention:  { weight: 0.0,  halfLifeDays: 14 },
  correction: { weight: 0.0,  halfLifeDays: Infinity },
};

const DAY_MS = 86_400_000;

export function decayedWeight(ev: SignalEvent, obs: Observation, now: number): number {
  if (ev.retractedAt) return 0;
  const base = obs.weight ?? SIGNAL[ev.class].weight;
  const { halfLifeDays } = SIGNAL[ev.class];
  if (!Number.isFinite(halfLifeDays)) return base;
  const ageDays = Math.max(0, (now - ev.at) / DAY_MS);
  return base * Math.pow(0.5, ageDays / halfLifeDays);
}

export function retract(
  ledger: readonly SignalEvent[], eventId: string, now: number,
): SignalEvent[] {
  return ledger.map((e) => (e.id === eventId && !e.retractedAt ? { ...e, retractedAt: now } : e));
}

/** Retract every signal a candidate produced. This is what makes "undo the
 *  change, and undo what it taught" a single action rather than two. */
export function retractByCandidate(
  ledger: readonly SignalEvent[], candidateId: string, now: number,
): SignalEvent[] {
  return ledger.map((e) =>
    e.cause?.candidateId === candidateId && !e.retractedAt ? { ...e, retractedAt: now } : e);
}
